import { Box, ButtonBase } from '@mui/material';

import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';

const ENTRY_POINT_EXPANSION_DURATION_MS = 175;
const ENTRY_POINT_LABEL_WAIT_MS = 150;
const ENTRY_POINT_LABEL_REVEAL_DURATION_MS = 50;
const ENTRY_POINT_COLLAPSE_DURATION_MS = 200;
const ENTRY_POINT_LABEL_REVEAL_DELAY_MS =
  ENTRY_POINT_EXPANSION_DURATION_MS + ENTRY_POINT_LABEL_WAIT_MS;

const ENTRY_POINT_WIDTH_PX = 36;
const ENTRY_POINT_HEIGHT_PX = 48;
const ENTRY_POINT_HOVER_WIDTH_PX = 40;
const ENTRY_POINT_HOVER_HEIGHT_PX = 52;
const ENTRY_POINT_ICON_SIZE_PX = 24;
// The icon renders at 20px at rest and scales up to its full 24px on hover.
const ENTRY_POINT_ICON_REST_SCALE = 20 / ENTRY_POINT_ICON_SIZE_PX;

interface FullScreenModalHouseholdChatEntryPointProps {
  bottomOffset: number;
  onClick: () => void;
}

export function FullScreenModalHouseholdChatEntryPoint({
  bottomOffset,
  onClick,
}: FullScreenModalHouseholdChatEntryPointProps) {
  const entryPointButton = (
    <ButtonBase
      aria-label="Open Luminary AI"
      data-testid="full-screen-modal-household-chat-entry-point"
      onClick={onClick}
      sx={{
        width: ENTRY_POINT_WIDTH_PX,
        minWidth: ENTRY_POINT_WIDTH_PX,
        height: ENTRY_POINT_HEIGHT_PX,
        borderRadius: '100px 0 0 100px',
        backgroundColor: COLORS.BRAND.ORANGE,
        boxShadow: THEME_SHADOWS.md,
        color: COLORS.PRIMITIVES.WHITE,
        transition: [
          `width ${ENTRY_POINT_COLLAPSE_DURATION_MS}ms linear`,
          `min-width ${ENTRY_POINT_COLLAPSE_DURATION_MS}ms linear`,
          `height ${ENTRY_POINT_COLLAPSE_DURATION_MS}ms linear`,
        ].join(', '),
        '&:hover, &.Mui-focusVisible': {
          width: ENTRY_POINT_HOVER_WIDTH_PX,
          minWidth: ENTRY_POINT_HOVER_WIDTH_PX,
          height: ENTRY_POINT_HOVER_HEIGHT_PX,
          transitionDuration: `${ENTRY_POINT_EXPANSION_DURATION_MS}ms`,
          transitionTimingFunction: 'ease-out',
          '& .full-screen-modal-household-chat-entry-point-icon': {
            transform: 'scale(1)',
            transitionDuration: `${ENTRY_POINT_EXPANSION_DURATION_MS}ms`,
            transitionTimingFunction: 'ease-out',
          },
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${COLORS.PRIMITIVES.WHITE}`,
          outlineOffset: -4,
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover, &.Mui-focusVisible': {
            transition: 'none',
            '& .full-screen-modal-household-chat-entry-point-icon': {
              transition: 'none',
            },
          },
          '& .full-screen-modal-household-chat-entry-point-icon': {
            transition: 'none',
          },
        },
      }}
    >
      <Box
        className="full-screen-modal-household-chat-entry-point-icon"
        sx={{
          display: 'flex',
          transform: `scale(${ENTRY_POINT_ICON_REST_SCALE})`,
          transition: `transform ${ENTRY_POINT_COLLAPSE_DURATION_MS}ms linear`,
        }}
      >
        <Stars01Icon
          size={ENTRY_POINT_ICON_SIZE_PX}
          fill={COLORS.PRIMITIVES.WHITE}
          color={COLORS.PRIMITIVES.WHITE}
        />
      </Box>
    </ButtonBase>
  );

  return (
    <Box
      data-testid="full-screen-modal-household-chat-entry-point-positioner"
      sx={{
        position: 'absolute',
        right: 0,
        bottom: bottomOffset,
        zIndex: 1,
        display: 'flex',
        // Centering the button in a fixed-height positioner lets the hover
        // height growth swell symmetrically around the icon instead of
        // pushing the button upward from its bottom edge.
        alignItems: 'center',
        height: ENTRY_POINT_HEIGHT_PX,
      }}
    >
      <Tooltip
        title="Luminary AI"
        placement="top-end"
        arrow={false}
        enterDelay={ENTRY_POINT_LABEL_REVEAL_DELAY_MS}
        enterNextDelay={ENTRY_POINT_LABEL_REVEAL_DELAY_MS}
        TransitionProps={{ timeout: ENTRY_POINT_LABEL_REVEAL_DURATION_MS }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-12, 8],
                },
              },
            ],
          },
          tooltip: {
            style: {
              margin: 0,
              padding: 4,
            },
          },
        }}
      >
        {entryPointButton}
      </Tooltip>
    </Box>
  );
}
